/**
 * Get all collaborations that belong to your group
 */

export const collabTermsFragment = /* GraphQL */ `
  fragment CollabTerms on CollaborationTerms {
    id
    version
    type
    track {
      name
      id
      trackArtists {
        primary
        position
        artist {
          name
        }
      }
      product {
        artwork {
          small {
            url
            expiresAt
            expiresIn
          }
          medium {
            url
            expiresAt
            expiresIn
          }
        }
      }
    }
    acceptedAt
    declinedAt
    collaborationStartDate
    collaborationEndDate
    paymentStartDate
    paymentEndDate
    type
    fixedFee
    royaltySharePercentage
    createdByGroup {
      id
      name
      avatar {
        small {
          url
          expiresAt
        }
      }
    }
    posts {
      id
      platform {
        name
        type
        id
      }
      proofOfDeliveries {
        status
        id
      }
    }
    order {
      paypalTransactions {
        state
      }
    }
  }
`

export const myCollabItemFragment = /* GraphQl */ `
  fragment MyCollabItem on Collaboration {
    id
    createdAt
    canceledAt
    state
    from {
      id
      name
      avatar {
        small {
          url
          expiresAt
        }
      }
      artistCollaborationSettings {
        royaltyShareEnabled
        fixedFeeEnabled
        platforms {
          id
          platform {
            name
          }
        }
        genres {
          id
          genre {
            name
          }
        }
      }
      influencerCollaborationSettings {
        fixedFeeEnabled
        royaltyShareEnabled
        platforms {
          id
          platform {
            name
          }
        }
        genres {
          id
          genre {
            name
          }
        }
      }
    }
    to {
      id
      name
      avatar {
        small {
          url
          expiresAt
        }
      }
    }
    terms {
    ...CollabTerms
    }
  }

  ${collabTermsFragment}
`

export const getMyCollaborations = /* GraphQL */ `
  query GetMyCollaborations($groupId: ID!) {
    getMyCollaborations(groupId: $groupId) {
      ...MyCollabItem
    }
  }

  ${myCollabItemFragment}
`

/**
 * Get a specific collaboration by ID
 */
export const getCollaboration = /* GraphQL */ `
  query getCollaborationById($collaborationId: ID!) {
    getCollaboration(collaborationId: $collaborationId) {
      canceledAt
      canceledReason
      createdAt
      state
      id
      terms {
        id
        acceptedAt
        createdAt
        createdByGroup {
          id
          name
          avatar {
            small {
              url
              expiresAt
            }
          }
        }
        declinedAt
        declinedReason
        fixedFee
        message
        postFrequency
        postAmount
        postPeriod
        postsType
        collaborationStartDate
        collaborationEndDate
        paymentStartDate
        paymentEndDate
        royaltySharePercentage
        posts {
          id
          platform {
            name
            id
          }
          proofOfDeliveries {
            influencerMessage
            created
            status
            id
            draftUrl
            reasonForDeclining
            artistMessage
          }
        }
        track {
          id
          bpm
          name
          genre
          subgenre
          alternateGenre
          alternateSubgenre
          audioLength
          trackArtists {
            primary
            position
            artist {
              name
            }
          }
          product {
            artwork {
              small {
                url
                expiresAt
                expiresIn
              }
              medium {
                url
                expiresAt
                expiresIn
              }
            }
          }
          compressedAudio {
            url
            expiresAt
          }
          previewAudio {
            url
            expiresAt
          }
        }
        type
        version
        order {
          paypalTransactions {
            state
          }
        }
      }
      to {
        name
        id
        avatar {
          small {
            url
            expiresAt
          }
          medium {
            url
            expiresAt
          }
        }
      }
      from {
        id
        name
      }
    }
  }
`

/**
 * Get a collaboration with compressed audio for ongoing collabs
 */
export const getOngoingCollaboration = /* GraphQL */ `
  query getCollaboration($collaborationId: ID!) {
    getCollaboration(collaborationId: $collaborationId) {
      canceledAt
      canceledReason
      createdAt
      id
      state
      terms {
        id
        acceptedAt
        createdAt
        createdByGroup {
          id
          name
          avatar {
            small {
              url
              expiresAt
            }
          }
        }
        declinedAt
        declinedReason
        fixedFee
        message
        postFrequency
        postAmount
        postPeriod
        postsType
        collaborationStartDate
        collaborationEndDate
        paymentStartDate
        paymentEndDate
        royaltySharePercentage
        posts {
          id
          platform {
            name
            id
          }
          proofOfDeliveries {
            status
            id
          }
        }
        track {
          id
          bpm
          name
          genre
          subgenre
          alternateGenre
          alternateSubgenre
          audioLength
          trackArtists {
            primary
            position
            artist {
              name
            }
          }
          product {
            artwork {
              small {
                url
                expiresAt
                expiresIn
              }
              medium {
                url
                expiresAt
                expiresIn
              }
            }
          }
          compressedAudio {
            url
            expiresAt
          }
          previewAudio {
            url
            expiresAt
          }
        }
        type
        version
        order {
          paypalTransactions {
            state
          }
        }
      }
      to {
        name
        id
        avatar {
          small {
            url
            expiresAt
          }
          medium {
            url
            expiresAt
          }
        }
      }
      from {
        id
        name
      }
    }
  }
`

/**
 * Add a colloboration (new version)
 */
export const addCollaboration = /* GraphQL */ `
  mutation AddCollaboration($input: CollaborationInput) {
    addCollaboration(input: $input) {
      id
    }
  }
`

/**
 * Add a colloboration counter proposal
 */
export const createCollaborationCounterProposal = /* GraphQL */ `
  mutation CreateCollaborationCounterProposal(
    $collaborationId: ID!
    $collaborationTermsId: ID!
    $input: CollaborationCounterProposalInput
  ) {
    createCollaborationCounterProposal(
      collaborationId: $collaborationId
      collaborationTermsId: $collaborationTermsId
      input: $input
    ) {
      id
    }
  }
`

/**
 * Decline a colloboration
 */
export const declineCollaboration = /* GraphQL */ `
  mutation DeclineCollaboration($collaborationId: ID!, $collaborationTermsId: ID!, $reason: String) {
    closeCollaboration(
      collaborationId: $collaborationId
      collaborationTermsId: $collaborationTermsId
      input: { status: DECLINED, reason: $reason }
    ) {
      terms {
        declinedAt
      }
    }
  }
`

/**
 * Cancel a colloboration
 */
export const cancelCollaboration = /* GraphQL */ `
  mutation CancelCollaboration($collaborationId: ID!, $collaborationTermsId: ID!, $reason: String) {
    closeCollaboration(
      collaborationId: $collaborationId
      collaborationTermsId: $collaborationTermsId
      input: { status: CANCELED, reason: $reason }
    ) {
      canceledAt
    }
  }
`
/**
 * Accept a colloboration
 */
export const acceptCollaboration = /* GraphQL */ `
  mutation AcceptCollaboration($collaborationId: ID!, $collaborationTermsId: ID!) {
    closeCollaboration(
      collaborationId: $collaborationId
      collaborationTermsId: $collaborationTermsId
      input: { status: ACCEPTED }
    ) {
      terms {
        acceptedAt
      }
    }
  }
`

/**
 * Get proof of delivery details
 */
export const getProofOfDelivery = /* GraphQL */ `
  query GetProofOfDelivery($proofOfDeliveryId: ID!) {
    getProofOfDelivery(id: $proofOfDeliveryId) {
      url
      status
      influencerMessage
      created
      draftInfluencerMessage
      draftUrl
      reasonForDeclining
      artistMessage
    }
  }
`
export const createCollaborationOrder = /* GraphQL */ `
  mutation CreateCollaborationOrder($input: CreateCollaborationOrderInput!) {
    createCollaborationOrder(input: $input)
  }
`
export const capturePaypalOrder = /* GraphQL */ `
  mutation CapturePaypalOrderCollab($orderId: String!) {
    capturePaypalOrder(paypalOrderId: $orderId)
  }
`

export const addDraftDelivery = /* GraphQL */ `
  mutation AddDraftDelivery($collaborationPostId: ID!, $input: AddDraftDeliveryInput) {
    addDraftDelivery(collaborationPostId: $collaborationPostId, input: $input) {
      id
    }
  }
`

export const reviewDraftDelivery = /* GraphQL */ `
  mutation ReviewDraftDelivery($id: ID!, $artistMessage: String!) {
    reviewDraftDelivery(id: $id, input: { artistMessage: $artistMessage, status: DRAFT_ACCEPTED }) {
      id
    }
  }
`

export const declineDraftDelivery = /* GraphQL */ `
  mutation DeclineDraftDelivery($id: ID!, $artistMessage: String!, $reasonForDeclining: String) {
    reviewDraftDelivery(
      id: $id
      input: { artistMessage: $artistMessage, reasonForDeclining: $reasonForDeclining, status: DRAFT_FEEDBACK }
    ) {
      id
    }
  }
`

export const finalizeDelivery = /* GraphQL */ `
  mutation FinalizeDelivery($id: ID!, $input: FinalizeDeliveryInput) {
    finalizeDelivery(id: $id, input: $input) {
      id
    }
  }
`
